import React, { useContext, useOptimistic, useState } from 'react';
import { T, useGT } from 'gt-react';
import { CompanyDTO } from 'src/api/core/companies/useCompany';
import { useSupplierTags } from 'src/api/core/enumOptionAssociations/useEnumOptionAssociations';
import { TagsDisplay } from 'src/components/rebates/TagsDisplay';
import { SupplierPaymentDetailsSection } from '@/components/company/SupplierPaymentDetailsSection';
import { useSupplierPaymentDetails } from '@/api/rpc/hooks/accounts-payable/useSupplierPaymentDetails';
import { AuthContext } from '@/context/auth.context';
import { useViewMode } from '@/context/view-mode.context';
import { isAdmin } from '@/utils/user';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { CopyIcon, CheckIcon, PencilSimpleIcon } from '@phosphor-icons/react';
import { useCan } from '@/hooks/useCan';
import { Action, Subject } from '@erp/authz';
import useUpdateSupplier from 'src/api/accounts-payable/useUpdateSupplier';
import { SupplierStatus } from 'src/api/accounts-payable/useCreateSupplier';

interface InfoCardProps {
  label: string;
  value?: string | null;
}

const InfoCard: React.FC<InfoCardProps> = ({ label, value }) => (
  <div className="p-4 border rounded-lg">
    <div className="text-xs font-bold text-gray-600 mb-1">{label}</div>
    <div className="text-sm">{value || '-'}</div>
  </div>
);

interface SupplierInfoProps {
  company: CompanyDTO;
  variant?: 'sidebar' | 'tab';
  onRefresh?: () => void;
}

export const SupplierInfo: React.FC<SupplierInfoProps> = ({
  company,
  variant = 'tab',
  onRefresh,
}) => {
  const gt = useGT();
  const { currentUser } = useContext(AuthContext);
  const { viewMode } = useViewMode();
  const userIsAdmin = isAdmin(currentUser, viewMode);
  const [copied, setCopied] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [canUpdateSupplier] = useCan(Action.Update, Subject.Supplier);

  const supplier = company.supplier;

  const [optimisticSupplier, setOptimisticSupplier] = useOptimistic(
    supplier,
    (
      current,
      update: {
        name: string;
        contactName: string;
        email: string;
        phone: string;
        status: string;
      },
    ) => (current ? { ...current, ...update } : current),
  );

  const [editForm, setEditForm] = useState({
    name: supplier?.name ?? '',
    contact_name: supplier?.contactName ?? '',
    email: supplier?.email ?? '',
    phone: supplier?.phone ?? '',
    status: (supplier?.status as SupplierStatus) ?? SupplierStatus.ACTIVE,
  });

  const { paymentDetails, isLoading: isPaymentDetailsLoading } =
    useSupplierPaymentDetails(supplier?.id ?? '');
  const hasBankAccounts = paymentDetails.length > 0;

  const updateSupplierMutation = useUpdateSupplier(supplier?.id ?? '', () => {
    setIsEditing(false);
    onRefresh?.();
  });

  const handleStartEdit = () => {
    setEditForm({
      name: supplier?.name ?? '',
      contact_name: supplier?.contactName ?? '',
      email: supplier?.email ?? '',
      phone: supplier?.phone ?? '',
      status: (supplier?.status as SupplierStatus) ?? SupplierStatus.ACTIVE,
    });
    setIsEditing(true);
  };

  const emailRequiredButMissing = hasBankAccounts && !editForm.email.trim();
  const emailRequirementUnknown =
    isPaymentDetailsLoading && !editForm.email.trim();

  const handleSave = () => {
    if (emailRequiredButMissing || emailRequirementUnknown) return;
    setOptimisticSupplier({
      name: editForm.name,
      contactName: editForm.contact_name,
      email: editForm.email,
      phone: editForm.phone,
      status: editForm.status,
    });
    updateSupplierMutation.mutate({
      name: editForm.name,
      contact_name: editForm.contact_name || undefined,
      email: editForm.email || undefined,
      phone: editForm.phone || undefined,
      status: editForm.status,
    });
  };

  const {
    currentTags,
    availableTags,
    currentTagIds,
    isLoading,
    isUpdating,
    handleAddTag,
    handleRemoveTag,
  } = useSupplierTags(company.supplier?.id);

  const handleCopyUuid = () => {
    if (!company.supplier?.id) return;
    navigator.clipboard.writeText(company.supplier.id).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (!company.supplier) {
    return (
      <div className="text-gray-500 text-center py-8">
        <T>No supplier information available</T>
      </div>
    );
  }

  if (variant === 'sidebar') {
    return (
      <div className="p-6">
        <h2 className="text-xl font-medium mb-6">
          <T context="section heading for supplier information">
            Supplier Information
          </T>
        </h2>
        {userIsAdmin && company.supplier?.id && (
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-bold text-gray-600">UUID</span>
              <Badge variant="secondary" className="text-xs">
                <T context="admin badge label">Admin</T>
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm">{company.supplier.id}</span>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={handleCopyUuid}
                    >
                      {copied ? (
                        <CheckIcon className="h-3 w-3 text-green-600" />
                      ) : (
                        <CopyIcon className="h-3 w-3" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>
                      {copied
                        ? gt('Copied!', {
                            context: 'copy UUID button tooltip after copying',
                          })
                        : gt('Copy UUID', {
                            context: 'copy UUID button tooltip',
                          })}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        )}
        {/* <InfoRow label="Contact Name" value={company.supplier.contactName} />
        <InfoRow label="Phone" value={company.supplier.phone} /> */}
        {/* <InfoRow label="Tax ID" value={company.supplier.taxId} />
        <InfoRow label="Payment Terms" value={company.supplier.paymentTerms} /> */}
        {company.supplier?.id && (
          <div className="mb-4">
            <span className="text-xs font-bold text-gray-600 block mb-2">
              <T context="label for supplier tags section">Tags</T>
            </span>
            <TagsDisplay
              currentTags={currentTags}
              availableTags={availableTags}
              currentTagIds={currentTagIds}
              isLoading={isLoading}
              isUpdating={isUpdating}
              onAddTag={handleAddTag}
              onRemoveTag={handleRemoveTag}
              layout="compact"
            />
          </div>
        )}
      </div>
    );
  }

  // Tab variant (default)
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium">
          <T context="section heading for supplier information">
            Supplier Information
          </T>
        </h3>
        {canUpdateSupplier && !isEditing && (
          <Button size="sm" onClick={handleStartEdit}>
            <PencilSimpleIcon className="h-3 w-3 mr-1" />
            <T context="button label to edit supplier information">Edit</T>
          </Button>
        )}
        {isEditing && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsEditing(false)}
            >
              <T context="button label to cancel editing supplier information">
                Cancel
              </T>
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={
                !editForm.name.trim() ||
                emailRequirementUnknown ||
                emailRequiredButMissing ||
                updateSupplierMutation.isPending
              }
            >
              {updateSupplierMutation.isPending
                ? gt('Saving...', {
                    context: 'save supplier info button label while saving',
                  })
                : gt('Save', { context: 'save supplier info button label' })}
            </Button>
          </div>
        )}
      </div>
      <div className="space-y-4">
        {isEditing ? (
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 border rounded-lg">
              <label className="text-xs font-bold text-gray-600 block mb-1">
                <T context="label for supplier name input">Supplier Name *</T>
              </label>
              <Input
                value={editForm.name}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, name: e.target.value }))
                }
              />
            </div>
            <div className="p-4 border rounded-lg">
              <label className="text-xs font-bold text-gray-600 block mb-1">
                <T context="label for supplier status select">Status</T>
              </label>
              <Select
                value={editForm.status}
                onValueChange={(val) =>
                  setEditForm((f) => ({
                    ...f,
                    status: val as SupplierStatus,
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={SupplierStatus.ACTIVE}>
                    <T context="supplier status option">Active</T>
                  </SelectItem>
                  <SelectItem value={SupplierStatus.INACTIVE}>
                    <T context="supplier status option">Inactive</T>
                  </SelectItem>
                  <SelectItem value={SupplierStatus.ON_HOLD}>
                    <T context="supplier status option">On Hold</T>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="p-4 border rounded-lg">
              <label className="text-xs font-bold text-gray-600 block mb-1">
                <T context="label for contact name input">Contact Name</T>
              </label>
              <Input
                value={editForm.contact_name}
                onChange={(e) =>
                  setEditForm((f) => ({
                    ...f,
                    contact_name: e.target.value,
                  }))
                }
              />
            </div>
            <div className="p-4 border rounded-lg">
              <label className="text-xs font-bold text-gray-600 block mb-1">
                <T context="label for email input">Email</T>
              </label>
              <Input
                type="email"
                value={editForm.email}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, email: e.target.value }))
                }
              />
              {emailRequiredButMissing && (
                <p className="text-xs text-red-600 mt-1">
                  <T>Email is required while bank accounts are on file</T>
                </p>
              )}
            </div>
            <div className="p-4 border rounded-lg">
              <label className="text-xs font-bold text-gray-600 block mb-1">
                <T context="label for phone input">Phone</T>
              </label>
              <Input
                value={editForm.phone}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, phone: e.target.value }))
                }
              />
            </div>
            <InfoCard
              label={gt('Tax ID', { context: 'supplier info field label' })}
              value={company.supplier.taxId}
            />
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4">
            <InfoCard
              label={gt('Supplier Name', {
                context: 'supplier info field label',
              })}
              value={optimisticSupplier?.name}
            />
            <InfoCard
              label={gt('Status', { context: 'supplier info field label' })}
              value={optimisticSupplier?.status}
            />
            {userIsAdmin && company.supplier?.id && (
              <div className="p-4 border rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <div className="text-xs font-bold text-gray-600">UUID</div>
                  <Badge variant="secondary" className="text-xs">
                    <T context="admin badge label">Admin</T>
                  </Badge>
                </div>
                <div className="flex items-center gap-2">
                  <div className="text-sm">{company.supplier.id}</div>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={handleCopyUuid}
                        >
                          {copied ? (
                            <CheckIcon className="h-3 w-3 text-green-600" />
                          ) : (
                            <CopyIcon className="h-3 w-3" />
                          )}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>
                          {copied
                            ? gt('Copied!', {
                                context:
                                  'copy UUID button tooltip after copying',
                              })
                            : gt('Copy UUID', {
                                context: 'copy UUID button tooltip',
                              })}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>
            )}
            <InfoCard
              label={gt('Contact Name', {
                context: 'supplier info field label',
              })}
              value={optimisticSupplier?.contactName}
            />
            <InfoCard
              label={gt('Email', { context: 'supplier info field label' })}
              value={optimisticSupplier?.email}
            />
            <InfoCard
              label={gt('Phone', { context: 'supplier info field label' })}
              value={optimisticSupplier?.phone}
            />
            <InfoCard
              label={gt('Tax ID', { context: 'supplier info field label' })}
              value={optimisticSupplier?.taxId}
            />
          </div>
        )}
        {company.supplier.paymentTerms && (
          <InfoCard
            label={gt('Payment Terms', {
              context: 'supplier info field label',
            })}
            value={company.supplier.paymentTerms}
          />
        )}
        {company.supplier?.id && (
          <div className="p-4 border rounded-lg">
            <div className="text-xs font-bold text-gray-600 mb-2">
              <T context="label for supplier tags section">Tags</T>
            </div>
            <TagsDisplay
              currentTags={currentTags}
              availableTags={availableTags}
              currentTagIds={currentTagIds}
              isLoading={isLoading}
              isUpdating={isUpdating}
              onAddTag={handleAddTag}
              onRemoveTag={handleRemoveTag}
              layout="compact"
            />
          </div>
        )}
        {company.supplier?.id && (
          <div className="p-4 border rounded-lg">
            <SupplierPaymentDetailsSection
              supplierId={company.supplier.id}
              supplierEmail={optimisticSupplier?.email ?? null}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default SupplierInfo;
